import dayjs from 'dayjs';
import queryStringParser from 'query-string';
import isUuid from 'is-uuid';
import { countBy, keyBy, maxBy, sortBy, uniq, uniqBy } from 'lodash-es';

import {
  STEPPER_FLOWS,
  AI_EDITOR_QUERY_PARAM_KEYS,
} from 'components/ReportViewComponents/constants';
import { constructURL, extractEntityName } from 'utils';
import { MDGEN_SYSTEM_TYPES } from 'constants';
import {
  flattenEntityType,
  getEntityKey,
  getEntityTypeFromSetName,
  getLinkableFields,
} from 'odata/utils';

export const WORKER_FETCH_STATE = {
  IDLE: 'IDLE',
  FETCHING: 'FETCHING',
  IMPORTING: 'IMPORTING',
  READY: 'READY',
  FETCH_FAILED: 'FETCH_FAILED',
  IMPORT_FAILED: 'IMPORT_FAILED',
};

export const initialWorkerState = {
  changeId: null,
  fetchState: WORKER_FETCH_STATE.IDLE,
  recordsFetched: 0,
  totalRecords: 0,
};

/**
 * Reads the linked_query block ({ queries, linked_by, linked_filters }) of a bookmark or
 * log. Records saved before multi-entity support have no linked_query, only query_string,
 * so they become a single entry.
 *
 * @param {Object} record - A bookmark or query log.
 * @param {string} [legacyQueryString] - URL to use when the record has no linked_query.
 */
export function getSavedQuery(record, legacyQueryString = record.query_string) {
  return record.linked_query?.queries?.length
    ? record.linked_query
    : { queries: [{ query_string: legacyQueryString }] };
}

const DISALLOWED_PARAMS = ['$count', '$inlinecount', '$top', '$skip'];

/**
 * Strip disallowed query params before applying to report view.
 *
 * NOTE: We intentionally avoid `new URL()` / `URLSearchParams` here because
 * calling `searchParams.delete()` re-serializes the entire query string with
 * `application/x-www-form-urlencoded` encoding, which encodes `$` → `%24`
 * in param keys (e.g. `$filter` → `%24filter`) and spaces → `+`.
 * The downstream parser (`query-string` with `decode: false`) then can't
 * find OData params like `$filter`, `$select`, etc., breaking hydration.
 *
 * @param {string} queryString  Raw OData query string
 * @returns {string}
 */
export function sanitizeQueryString(queryString) {
  const questionMarkIndex = queryString.indexOf('?');
  if (questionMarkIndex === -1) {
    return queryString;
  }

  const path = queryString.slice(0, questionMarkIndex);
  const search = queryString.slice(questionMarkIndex + 1);

  const cleanParams = search
    .split('&')
    .filter((param) => !DISALLOWED_PARAMS.includes(param.split('=')[0]));

  return cleanParams.length > 0 ? `${path}?${cleanParams.join('&')}` : path;
}

// Linked filters span entities, so each field is shown as "Entity.field".
export function prefixRulesWithEntityName(rules, entityKeyToName) {
  return rules.map((rule) =>
    rule.rules
      ? { ...rule, rules: prefixRulesWithEntityName(rule.rules, entityKeyToName) }
      : {
          ...rule,
          property: {
            ...rule.property,
            name: `${entityKeyToName[rule.entity]}.${rule.property?.name}`,
          },
        }
  );
}

// linked_query URLs are relative to the base endpoint; query_string is a full URL.
export function getQueryEntityName(queryString, baseEndpoint) {
  const urlObj = constructURL(queryString) ?? constructURL(`${baseEndpoint}/${queryString}`);
  return (urlObj && extractEntityName(urlObj)) || null;
}

// "User, PerPersonal and 2 more"
export function getEntityNamesLabel(entityNames) {
  if (entityNames.length <= 2) {
    return entityNames.join(', ');
  }

  return `${entityNames.slice(0, 2).join(', ')} and ${entityNames.length - 2} more`;
}

/**
 * How the entities of a saved multi-entity query are linked; null for a single entity.
 *
 * @param {Object} savedQuery - { queries, linked_by, linked_filters }
 * @param {Array<string>} entityNames - One per query, in the same order.
 * @returns {{label: string, linkedEntityNames?: Array<string>|null, hasFilter?: boolean}|null}
 */
export function getSavedQueryLinkScope(
  { queries, linked_by: linkedBy, linked_filters: linkedFilters },
  entityNames
) {
  if (queries.length < 2) {
    return null;
  }

  const linkedEntityKeys = uniq(
    (linkedBy ?? []).flatMap(({ from, to }) => [from.entity_key, to.entity_key])
  );

  if (linkedEntityKeys.length < 2) {
    return { label: 'Not linked' };
  }

  const areAllEntitiesLinked = linkedEntityKeys.length === queries.length;

  return {
    label: areAllEntitiesLinked ? 'All entities linked' : 'Linked:',
    linkedEntityNames: areAllEntitiesLinked
      ? null
      : entityNames.filter((_, index) => linkedEntityKeys.includes(queries[index].entity)),
    hasFilter: Boolean(linkedFilters?.rules?.length),
  };
}

/**
 * Generates a database name prefix for the current tab. This is so that two
 * databases in different tabs do not collide which can cause consistency issues.
 */
export function generateDatabaseNamePrefix({ tabId }) {
  return `__integrtr_dataflow-${tabId}-`;
}

/**
 * Function that parses the query url and returns the entity name from it
 *
 * @param {string} url the query url from which you want to extract the entity
 * @returns {string} the entity name
 */
export function extractEntityFromQueryString(url) {
  const match = url.match(/v2\/([^?]+)/);
  return match ? match[1] : null;
}

/**
 * The entity sets an export job reads: one from query_string, or one per linked_query query.
 *
 * @param {Object} additionalInfo - additional_info of the job
 * @returns {string[]} Entity set names
 */
export function getExportJobEntityNames(additionalInfo) {
  if (additionalInfo?.linked_query) {
    return additionalInfo.linked_query.queries.map(
      ({ query_string }) => query_string.split('?')[0].split('(')[0]
    );
  }

  const entityName = extractEntityFromQueryString(additionalInfo?.query_string ?? '');
  return entityName ? [entityName] : [];
}

/**
 * The entities of an export that in-progress jobs are already exporting, alone or with other
 * entities.
 *
 * @param {string[]} entityNames - Entity set names the new export reads
 * @param {Object[]} jobsInProgress - Jobs that are still running
 * @returns {string[]} Entity set names already being exported
 */
export function getEntitiesAlreadyInExport(entityNames, jobsInProgress) {
  const exporting = new Set(
    jobsInProgress.flatMap((job) => getExportJobEntityNames(job.additional_info))
  );
  return entityNames.filter((name) => exporting.has(name));
}

/**
 * Builds a compact `entity?$param=value&...` context string from the editor query URL,
 * keeping only AI_EDITOR_QUERY_PARAM_KEYS. Returns the bare entity when no params apply.
 *
 * @param {string} queryUrl - The full editor query URL from the report view state.
 * @param {string} baseEndpoint - The connection's OData base endpoint; URL must start with it.
 * @returns {string|null} The context string, or null when the URL is missing/not for this endpoint.
 */
export function extractEditorOdataQuery(queryUrl, baseEndpoint) {
  if (typeof queryUrl !== 'string' || !queryUrl.startsWith(`${baseEndpoint}/`)) {
    return null;
  }
  const entity = extractEntityFromQueryString(queryUrl);
  if (!entity) {
    return null;
  }
  const { query } = queryStringParser.parseUrl(queryUrl, { decode: true });
  const keptParams = AI_EDITOR_QUERY_PARAM_KEYS.reduce((params, key) => {
    if (query[key] !== undefined) {
      params[key] = query[key];
    }
    return params;
  }, {});
  const paramString = queryStringParser.stringify(keptParams, { encode: false, sort: false });
  return paramString ? `${entity}?${paramString}` : entity;
}

export const setStepperDialogPrimaryBtnText = (activeStep, processLength, finalStepBtnText) => {
  const finalStep = processLength - 1;
  if (activeStep === finalStep) {
    return finalStepBtnText;
  }
  return 'Next';
};

export const getStepperSteps = (stepperFlow) => {
  switch (stepperFlow) {
    case STEPPER_FLOWS.SYSTEM_CONFIGURATION.KEY:
      return STEPPER_FLOWS.SYSTEM_CONFIGURATION.STEPS;

    default:
      return null;
  }
};

export const EXPORT_FILE_PASSWORD_MAX_LENGTH = 32;

export const checkPasswordStrength = (currentString, isPasswordFieldRequired) => {
  const value = currentString ?? '';

  // Cap first so whitespace-only values over the limit get the max-length error,
  // not the empty-field path from the trim checks below.
  if (value.length > EXPORT_FILE_PASSWORD_MAX_LENGTH) {
    return {
      isPasswordValid: false,
      helperText: 'Max length allowed is 32 characters',
    };
  }

  // Optional: any value under the max length is accepted (including empty).
  if (!isPasswordFieldRequired) {
    return { isPasswordValid: true, helperText: '' };
  }

  // Required: empty check, then strength rules.
  if (value.trim() === '') {
    return { isPasswordValid: false, helperText: 'Password Field cannot be empty' };
  }

  switch (true) {
    case value.length < 8:
      return {
        isPasswordValid: false,
        helperText: 'Length of password should be at least 8 characters',
      };
    case !/[!@#$%^&*(),.?":{}|<>]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 special character',
      };
    case !/\d/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 number',
      };
    case !/[A-Z]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 uppercase letter',
      };
    case !/[a-z]/.test(value):
      return {
        isPasswordValid: false,
        helperText: 'Should contain at least 1 lowercase letter',
      };
    default:
      return { isPasswordValid: true, helperText: '' };
  }
};

export const groupByMultiplicity = (array) => {
  return array.reduce((groupedColumnsArr, item) => {
    const multiplicity = item.multiplicity !== undefined ? item.multiplicity : 'base';
    const group = groupedColumnsArr.find((eachGroup) => eachGroup.multiplicity === multiplicity);
    if (group) {
      group.items.push(item);
    } else {
      groupedColumnsArr.push({ multiplicity, items: [item] });
    }
    return groupedColumnsArr;
  }, []);
};

/**
 * Finds the default collection from an array of collections.
 *
 * The function prioritizes finding a collection based on the following rules:
 * 1. If a collection has 'is_default' set to `true`, it is returned.
 * 2. If a collection is named 'Default Collection', it is returned.
 * 3. If neither of the above conditions are satisfied, the first collection
 *    in the array is returned as a fallback.
 *
 * @param {Array<Object>} collections - The list of collection objects to search.

 * @returns {Object|undefined} - The default collection object if found, or the
 * first collection in the list as a fallback. Returns `undefined` if the input
 * array is empty.
 */
export const getDefaultCollection = (collections = []) => {
  return (
    collections.find(
      (collection) => collection.is_default || collection.name === 'Default Collection'
    ) || collections[0]
  );
};

/**
 * Determines whether an entity, extracted from a given query URL,
 * is already present in the report view bookmarks.
 *
 * This function builds a Set of all entity names extracted from the bookmarks
 * and checks if the entity from the provided `queryUrl` exists in that Set.
 *
 * @param {Array<Object>} reportViewBookmarks - An array of bookmark collections.
 *   Each collection should have a `bookmarks` array, where each bookmark contains
 *   a `query_string` property.
 * @param {string} queryUrl - The query URL representing the entity to check for.
 *
 * @returns {boolean} - Returns `true` if the entity extracted from `queryUrl`
 *   exists in the bookmarks, otherwise `false`.
 *
 */
/**
 * The bookmark's primary OData query: query_string for Query View and older rows, otherwise
 * the first linked_query entry, which is relative to the connection's base endpoint.
 */
export function getBookmarkPrimaryQueryString(bookmark) {
  return bookmark.query_string ?? bookmark.linked_query?.queries?.[0]?.query_string ?? null;
}

/**
 * The entities a bookmark covers, in any order, e.g. "EmpJob" or "EmpJob, PerPerson".
 * Bookmarks only match for updating when they cover the same entities.
 */
export function getBookmarkEntityCode(bookmark) {
  if (bookmark.query_string) {
    return extractEntityFromQueryString(bookmark.query_string);
  }

  const entityCodes = (bookmark.linked_query?.queries ?? []).map(
    ({ query_string }) => query_string.split('?')[0]
  );

  return entityCodes.length ? [...entityCodes].sort().join(', ') : null;
}

export const isEntityBookmarked = (reportViewBookmarks = [], entityCode) => {
  // Set contains the names of all entities extracted from the bookmarks.
  const entitySet = new Set(
    reportViewBookmarks.flatMap((collection) => collection.bookmarks.map(getBookmarkEntityCode))
  );

  return entitySet.has(entityCode);
};

/**
 * Filters and returns all bookmarks from the report view collections
 * that match the entity extracted from the given query URL.
 *
 * Each matching bookmark is enriched with its corresponding `collection_name`.
 *
 * @param {Array<Object>} reportViewBookmarks - An array of bookmark collections.
 *   Each collection should have a `name` and a `bookmarks` array property.
 * @param {string} queryUrl - The query URL whose entity will be matched against the bookmarks.
 *
 * @returns {Array<Object>} - An array of bookmarks that belong to the same entity as the one
 *   extracted from the `queryUrl`. Each bookmark includes a `collection_name` field.
 *
 */
export const getBookmarksByEntityCode = (reportViewBookmarks = [], entityCode) => {
  const entityMap = new Map();

  reportViewBookmarks.forEach(({ name: collectionName, bookmarks }) => {
    bookmarks.forEach((bookmark) => {
      const entity = getBookmarkEntityCode(bookmark);

      if (entity) {
        // bookmark with collection name
        const enrichedBookmark = { ...bookmark, collection_name: collectionName };
        if (!entityMap.has(entity)) {
          entityMap.set(entity, [enrichedBookmark]);
        } else {
          entityMap.get(entity).push(enrichedBookmark);
        }
      }
    });
  });

  return entityMap.get(entityCode) || [];
};

/**
 * Removes parentheses surrounding values following the 'in' operator in a string.
 * This only applies for SF systems working on top of OData v2 protocol.
 *
 * This is typically used to adjust OData v2 filter strings, e.g., converting:
 *   "$filter=field in ('a','b')" -> "$filter=field in 'a','b'"
 *
 * Example:
 *   removeParenthesesAfterIn("status in ('active','inactive')")
 *   // returns "status in 'active','inactive'"
 *
 * @param {string} str - The input string possibly containing 'in (...)' expressions.
 * @returns {string} The modified string with parentheses after 'in' removed.
 */

export function removeParenthesesAfterIn(str) {
  return str.replace(/ in \(([^)]*)\)/g, ' in $1');
}

/**
 * Decodes an OData request URL so query values use spaces instead of literal '+'.
 */
export function decodeODataRequestUrl(url) {
  if (!url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const queryString = urlObj.searchParams.toString();
    const base = `${urlObj.origin}${urlObj.pathname}`;

    return queryString ? `${base}?${queryString}` : base;
  } catch {
    return removeParenthesesAfterIn(url);
  }
}

/**
 * Normalizes SF OData v2 `in` filter syntax on a query URL.
 * Decodes $filter before transforming (encoded URLs won't match the raw regex).
 */
export function normalizeSfODataQueryUrl(url, isSfSystem) {
  if (!isSfSystem || !url) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const filter = urlObj.searchParams.get('$filter');

    if (filter != null) {
      urlObj.searchParams.set('$filter', removeParenthesesAfterIn(filter));
    }

    return decodeODataRequestUrl(urlObj.toString());
  } catch {
    return removeParenthesesAfterIn(url);
  }
}

/**
 * Groups an array of conversations by date label relative to today.
 *
 * Conversations from today are grouped under "Today", those from yesterday
 * under "Yesterday", and all others under their formatted date (e.g. "Mar 20, 2026").
 *
 * @param {Array<Object>} conversations - The list of conversation objects.
 *   Each conversation should have a `created_at` property parseable by dayjs.
 * @returns {Object<string, Array<Object>>} An object keyed by date label,
 *   where each value is an array of conversations belonging to that date group.
 */
export function groupByDate(conversations) {
  const groups = {};
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  (conversations || []).forEach((conversation) => {
    const date = dayjs(conversation.created_at);
    let label;
    if (date.isAfter(today)) {
      label = 'Today';
    } else if (date.isAfter(yesterday)) {
      label = 'Yesterday';
    } else {
      label = date.format('MMM D, YYYY');
    }
    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(conversation);
  });

  return groups;
}

const MDGEN_TEMPLATE_VERSION_PATTERN = /^[1-9]\d*$/;

/**
 * Whether an MDGen template link carries a real template id and configuration version.
 *
 * @param {string} templateId
 * @param {string} configurationVersion
 * @returns {boolean}
 */
export function isValidMdgenTemplateLink(templateId, configurationVersion) {
  return (
    isUuid.anyNonNil(templateId ?? '') &&
    MDGEN_TEMPLATE_VERSION_PATTERN.test(configurationVersion ?? '')
  );
}

/**
 * Orders a template entity's active fields like MDGen lists them: key fields first and required
 * fields next, each by name, then the rest in template order.
 *
 * @param {Array<Object>} fields - Populated MDGen template fields.
 * @param {Array<string>} linkableFields - The entity's top-level key field names.
 * @returns {Array<Object>} Active fields, without pseudo fields, in display order.
 */
function getOrderedTemplateFields(fields, linkableFields) {
  const getFieldGroup = ({ field_code, additional_props }) => {
    if (linkableFields.includes(field_code)) {
      return 0;
    }

    return additional_props?.required === 'true' ? 1 : 2;
  };

  // sortBy is stable, so fields that are neither key nor required keep the template's order
  return sortBy(
    fields.filter(({ config }) => config?.active && !config.pseudoField),
    [
      getFieldGroup,
      (field) =>
        getFieldGroup(field) < 2 ? (field.field_name ?? field.field_code).toLowerCase() : '',
    ]
  );
}

/**
 * Reads one MDGen template sequence entry into the pieces a linked_query is built from.
 *
 * @param {Object} sequenceEntry - Populated template sequence entry ({ entity, fields }).
 * @param {Object} schema
 * @returns {Object|null} null when the entity isn't in the schema.
 */
function toTemplateEntity({ entity, fields }, schema) {
  const entityType = getEntityTypeFromSetName(entity.entity_code, schema);

  if (!entityType) {
    return null;
  }

  const flattenedEntity = flattenEntityType(entityType, schema.complexTypesMap);
  const linkableFields = getLinkableFields(flattenedEntity).map(({ name }) => name);
  const activeFields = getOrderedTemplateFields(fields, linkableFields);

  return {
    code: entity.entity_code,
    key: getEntityKey(flattenedEntity),
    linkableFields,
    fieldCodes: activeFields.map(({ field_code }) => field_code),
    references: activeFields
      .filter(({ config }) => config.references)
      .map(({ field_code, config }) => ({
        field: field_code,
        entity: config.references.entity,
        referencedField: config.references.field,
      })),
  };
}

/**
 * Builds a template entity's query string. Navigation field codes go in $select with a slash and
 * their navigation goes in $expand.
 *
 * @param {Object} templateEntity - { code, fieldCodes }
 * @returns {string} Query string relative to the service root.
 */
function getTemplateEntityQueryString({ code, fieldCodes }) {
  const navigationNames = uniq(
    fieldCodes
      .filter((fieldCode) => fieldCode.includes('.'))
      .map((fieldCode) => fieldCode.split('.')[0])
  );
  const queryParams = [
    fieldCodes.length && `$select=${fieldCodes.join(',').replaceAll('.', '/')}`,
    navigationNames.length && `$expand=${navigationNames.join(',')}`,
  ].filter(Boolean);

  return queryParams.length ? `${code}?${queryParams.join('&')}` : code;
}

/**
 * Picks the links a search can run with. A reference is a link only when both its fields are keys.
 * A search joins every link on one anchor field, so the most referenced entity field becomes the
 * anchor and other references leave their entities unlinked.
 *
 * @param {Array<Object>} templateEntities - { code, key, linkableFields, references }
 * @returns {Array<Object>} linked_by entries.
 */
function getTemplateLinkedBy(templateEntities) {
  const templateEntitiesByCode = keyBy(templateEntities, 'code');
  const toAnchorId = ({ entity, referencedField }) => `${entity}.${referencedField}`;

  const linkReferences = templateEntities.flatMap(({ code, linkableFields, references }) =>
    references
      .filter(
        (reference) =>
          reference.entity !== code &&
          linkableFields.includes(reference.field) &&
          templateEntitiesByCode[reference.entity]?.linkableFields.includes(
            reference.referencedField
          )
      )
      .map((reference) => ({ ...reference, from: code }))
  );

  const referenceCounts = countBy(linkReferences, toAnchorId);
  const anchorId = maxBy(Object.keys(referenceCounts), (candidate) => referenceCounts[candidate]);

  return uniqBy(
    linkReferences.filter((reference) => toAnchorId(reference) === anchorId),
    'from'
  ).map(({ from, field, entity, referencedField }) => ({
    from: { entity_key: templateEntitiesByCode[from].key, field },
    to: { entity_key: templateEntitiesByCode[entity].key, field: referencedField },
  }));
}

/**
 * Builds a linked_query from an MDGen template configuration: one query per SuccessFactors
 * entity with its active fields, linked by the template's field references.
 *
 * @param {Object} templateConfiguration - MDGen template configuration with a populated sequence.
 * @param {Object} options
 * @param {Object} options.schema
 * @param {string} [options.entityCode] - Builds only this entity when set.
 * @returns {Object|null} null when none of the template's entities are in the schema.
 */
export function buildLinkedQueryFromTemplateConfiguration(
  templateConfiguration,
  { schema, entityCode }
) {
  const templateEntities = (templateConfiguration?.configurations?.sequence ?? [])
    .filter(
      ({ entity, config }) =>
        config?.systemType !== MDGEN_SYSTEM_TYPES.SAP_HCM &&
        (!entityCode || entity.entity_code === entityCode)
    )
    .map((sequenceEntry) => toTemplateEntity(sequenceEntry, schema))
    .filter(Boolean);

  if (!templateEntities.length) {
    return null;
  }

  return {
    queries: templateEntities.map((templateEntity) => ({
      entity: templateEntity.key,
      query_string: getTemplateEntityQueryString(templateEntity),
    })),
    linked_by: getTemplateLinkedBy(templateEntities),
    linked_filters: null,
  };
}
